import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';

import { styles } from './styles';
import { SAMPLE_MADE_WITH_ITEMS } from './constants';
import {
  CheckMarkCell,
  InfoRow,
  WrappingInfoRow,
  TableRow,
  CompactTableCell,
  PDFLogo,
  ProductImage,
  PDFBarcode,
  CoordinatingSeparatesBox,
} from './PDFComponents';
import {
  formatSampleTypeDisplay,
  getBorderColor,
  formatDateUTC,
  getFabricSections,
} from './utils';
import { getImageUrlForPDF } from '../utils/imageUtils';

import { SampleTagResponse } from '../types';

export function DefaultSamplePDF({ tagData }: { tagData: SampleTagResponse }) {
  const sampleType = tagData?.type_of_sample_tag;
  const styleNumber = tagData?.style_number;
  const developmentType = tagData?.type_of_development;
  const productImageUrl = getImageUrlForPDF(
    tagData?.product_image,
    tagData?.productImageDataUrl,
  );

  // Derived values
  const borderColor = getBorderColor(sampleType, developmentType);
  const containerStyle = {
    ...styles.container,
    border: `15px solid ${borderColor}`,
  };
  const barcodeValue = tagData?.sample_tag_lpn || tagData?.id;

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={containerStyle}>
          {/* Main Content: Two Column Layout */}
          <View style={styles.mainContent}>
            {/* Left Column - All Content */}
            <View style={styles.leftColumn}>
              {/* Logo and Date Header */}
              <View style={styles.logoDateHeader}>
                <PDFLogo division={tagData?.division} logoWidth={170} />
                <Text
                  style={{
                    fontSize: 7,
                    marginLeft: 10,
                    alignSelf: 'flex-end',
                    fontWeight: 'medium',
                  }}
                >
                  Date: {formatDateUTC(tagData?.date_sent) || '-'}
                </Text>
              </View>

              {/* Horizontal Line */}
              <View style={[styles.horizontalLine, { marginBottom: 2 }]} />

              {/* Sample Type Header */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginBottom: 5,
                }}
              >
                <Text style={styles.sampleTypeHeader}>
                  {formatSampleTypeDisplay(sampleType).toUpperCase()}
                </Text>
                {developmentType && sampleType === 'Proto Development' && (
                  <Text style={styles.sampleSubType}>{developmentType}</Text>
                )}
              </View>

              {/* Product Details */}
              <InfoRow
                label="Vendor"
                value={
                  tagData?.company_name || tagData?.vendor_legal_name || '-'
                }
              />

              {/* Conditional PO Number */}
              {sampleType !== 'Proto Development' && (
                <InfoRow
                  label="PO #"
                  value={`${tagData?.po_number || '-'} (IHD: ${formatDateUTC(tagData?.po_due_date || tagData?.date_sent) || '-'})`}
                />
              )}

              <InfoRow
                label="Cut Date"
                value={
                  tagData?.cut_date ? formatDateUTC(tagData?.cut_date) : '-'
                }
              />

              <InfoRow label="Style #" value={styleNumber || '-'} />

              {/* Conditional Style Name for Proto Development */}
              {sampleType === 'Proto Development' && tagData?.style_name && (
                <WrappingInfoRow
                  label="Style Name"
                  value={tagData?.style_name}
                />
              )}

              {/* Conditional Style Name for other sample types */}
              {sampleType !== 'Proto Development' && (
                <WrappingInfoRow
                  label="Style Name"
                  value={tagData?.po_line_name || '-'}
                />
              )}

              <InfoRow
                label="Color/Wash"
                value={tagData?.po_line_color || tagData?.color_wash || '-'}
              />
              <InfoRow
                label="Size"
                value={tagData?.sample_size || tagData?.po_line_size || '-'}
              />
              <WrappingInfoRow
                label="Fabric Code & Name"
                value={tagData?.self?.fabric_code_name || '-'}
              />

              {/* Sample Made With Table */}
              <View style={{ marginTop: 10 }}>
                {/* Table Header */}
                <View style={styles.tableRow}>
                  <CompactTableCell
                    width="40%"
                    textStyle={{ fontSize: 5.5, fontWeight: 'semibold' }}
                    padding={3}
                  >
                    Sample Made With:
                  </CompactTableCell>
                  <CompactTableCell
                    width="30%"
                    textAlign="center"
                    textStyle={{ fontSize: 5.5 }}
                    padding={3}
                  >
                    Available
                  </CompactTableCell>
                  <CompactTableCell
                    width="30%"
                    hasBorder={false}
                    textAlign="center"
                    textStyle={{ fontSize: 5.5 }}
                    padding={3}
                  >
                    Production
                  </CompactTableCell>
                </View>

                {/* Table Rows */}
                {SAMPLE_MADE_WITH_ITEMS.map((key, index, array) => {
                  const isLastRow = index === array.length - 1;
                  const itemName = key
                    ? key
                        .replace('_', ' ')
                        .replace(/\b\w/g, (c: string) => c.toUpperCase())
                    : '';
                  const sampleCreatedWith = tagData?.sample_created_with?.[key];

                  return (
                    <TableRow key={key} isLast={isLastRow}>
                      <CompactTableCell
                        width="40%"
                        textStyle={{ fontSize: 7 }}
                        padding={3}
                      >
                        {itemName}
                      </CompactTableCell>
                      <CheckMarkCell
                        hasCheckMark={sampleCreatedWith === 'Available'}
                        hasBorder
                      />
                      <CheckMarkCell
                        hasCheckMark={sampleCreatedWith === 'Production'}
                        hasBorder={false}
                      />
                    </TableRow>
                  );
                })}
              </View>
            </View>

            {/* Right Column - Photo and Barcode */}
            <View style={styles.rightColumn}>
              <ProductImage imageUrl={productImageUrl} />
              <PDFBarcode value={barcodeValue} />
              {sampleType === 'TOP' &&
                tagData?.coordinating_separates &&
                tagData.coordinating_separates.length > 0 && (
                  <CoordinatingSeparatesBox
                    items={tagData.coordinating_separates}
                  />
                )}
            </View>
          </View>

          {/* Fabric Content & Weight Table */}
          <Text style={styles.sectionTitle}>Fabric Content & Weight:</Text>
          <View>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <CompactTableCell
                width="18%"
                textStyle={{ fontSize: 5.5, fontWeight: 'semibold' }}
                padding={3}
              >
                Type
              </CompactTableCell>
              <CompactTableCell
                width="62%"
                textStyle={{ fontSize: 5.5 }}
                padding={3}
              >
                Content
              </CompactTableCell>
              <CompactTableCell
                width="20%"
                hasBorder={false}
                textAlign="right"
                textStyle={{ fontSize: 5.5 }}
                padding={3}
              >
                Weight (GSM)
              </CompactTableCell>
            </View>

            {/* Table Rows */}
            {getFabricSections(tagData).map((section, index, array) => {
              const isLastRow = index === array.length - 1;
              return (
                <TableRow key={section.type} isLast={isLastRow}>
                  <CompactTableCell
                    width="18%"
                    textStyle={{
                      fontSize: 7,
                      backgroundColor: '#F8F8F8',
                      fontWeight: 'semibold',
                    }}
                    padding={3}
                  >
                    {section.type}
                  </CompactTableCell>
                  <CompactTableCell
                    width="62%"
                    textStyle={{ fontSize: 7 }}
                    padding={3}
                  >
                    {section.content}
                  </CompactTableCell>
                  <CompactTableCell
                    width="20%"
                    hasBorder={false}
                    textAlign="right"
                    textStyle={{ fontSize: 7 }}
                    padding={3}
                  >
                    {section.weight}
                  </CompactTableCell>
                </TableRow>
              );
            })}
          </View>

          {/* Comments Section */}
          <View style={styles.commentsSection}>
            <View style={styles.commentsLeft}>
              <Text
                style={{ fontSize: 7, fontWeight: 'semibold', marginBottom: 3 }}
              >
                Vendor Comments:
              </Text>
              <View style={{ height: 60 }}>
                <Text style={{ fontSize: 6, padding: 1 }}>
                  {tagData?.comments}
                </Text>
              </View>
            </View>

            {/* Vertical divider line */}
            <View style={styles.verticalDivider} />

            <View style={styles.commentsRight}>
              <Text style={styles.internalHeader}>FN INTERNAL ONLY</Text>
              <Text style={{ fontSize: 7, fontWeight: 'medium' }}>
                Comments:
              </Text>

              {/* Horizontal line above Date Received */}
              <View
                style={{
                  borderBottomWidth: 0.5,
                  borderBottomColor: '#000',
                  marginBottom: 2,
                  width: '100%',
                  marginTop: 40,
                }}
              />
              <Text style={{ fontSize: 7, fontWeight: 'medium' }}>
                Date Received:
              </Text>
              <View style={{ height: 10 }} />
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
}
